import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { GraduationCap, CheckCircle2, Loader2, Lock, Receipt } from "lucide-react";
import {
  LLB_MODULES,
  LLB_MODULE_PRICE_ZAR,
  LLB_BULK_DISCOUNT_THRESHOLD,
  LLB_BULK_DISCOUNT_RATE,
  priceLlbTutoring,
  type LlbModule,
} from "@/lib/llb-modules";
import {
  createLlbTutoringOrder,
  listMyLlbTutoringOrders,
} from "@/lib/api/llb-tutoring.functions";
import { launchPayfastCheckout } from "@/lib/payfast-launch";
import { LlbTipsWidget } from "@/components/llb/LlbTipsWidget";
import { LlbModuleDetailsModal } from "@/components/llb/LlbModuleDetailsModal";
import { UnisaRegistrationCard } from "@/components/llb/UnisaRegistrationCard";
import { LlbTailoredNotesPanel } from "@/components/llb/LlbTailoredNotesPanel";
import { PayFastBuyNowLlb } from "@/components/site/PayFastBuyNowLlb";
import { getMyUnisaStudentNumber } from "@/lib/api/llb-tutoring.functions";
import { Disclaimer } from "@/components/site/Disclaimer";
import { getMyLlbAccess } from "@/lib/api/llb-access.functions";


export const Route = createFileRoute("/_authenticated/portal/llb-tutoring")({
  head: () => ({
    meta: [
      { title: "UNISA LLB Tutoring | Nkadimeng Danny Education Division" },
      {
        name: "description",
        content:
          "Apply, pay and join tutored classes for any UNISA LLB module. R150 per module, 10% off when you take 4 or more in a semester. Pre-loaded study notes and daily AI-powered tips included.",
      },
    ],
  }),
  component: LlbTutoringPage,
});

const SEMESTERS = [
  { value: "2026-S1", label: "2026 — Semester 1" },
  { value: "2026-S2", label: "2026 — Semester 2" },
] as const;

function fmt(n: number) {
  return `R${n.toFixed(2)}`;
}

function LlbTutoringPage() {
  const [semester, setSemester] = useState<"2026-S1" | "2026-S2">("2026-S1");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  

  const createOrder = useServerFn(createLlbTutoringOrder);
  const listOrders = useServerFn(listMyLlbTutoringOrders);
  const getUnisa = useServerFn(getMyUnisaStudentNumber);

  const llbAccess = useServerFn(getMyLlbAccess);
  const llbAccessQ = useQuery({
    queryKey: ["my-llb-access"],
    queryFn: () => llbAccess(),
    staleTime: 60_000,
  });

  const ordersQ = useQuery({
    queryKey: ["my-llb-tutoring-orders"],
    queryFn: () => listOrders(),
  });

  const unisaQ = useQuery({
    queryKey: ["my-unisa-number"],
    queryFn: () => getUnisa(),
  });
  const hasUnisa = !!unisaQ.data?.unisaNumber;

  const [modalCode, setModalCode] = useState<string | null>(null);
  const modalModule = useMemo(
    () => LLB_MODULES.find((m) => m.code === modalCode) ?? null,
    [modalCode],
  );


  const grouped = useMemo(() => {
    const by: Record<string, LlbModule[]> = { "1": [], "2": [], "3": [], "4": [] };
    for (const m of LLB_MODULES) by[String(m.level)].push(m);
    return by;
  }, []);

  const pricing = priceLlbTutoring(selected.size);

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function handlePay() {
    if (!hasUnisa) {
      toast.error("Add your UNISA student number first — LLB tutoring is for registered UNISA students.");
      return;
    }
    if (selected.size === 0) {
      toast.error("Pick at least one module first.");
      return;
    }
    setSubmitting(true);
    try {
      const order = await createOrder({
        data: { semester, moduleCodes: Array.from(selected) },
      });
      toast.success(`Order created — ${order.reference}. Redirecting to PayFast…`);
      await launchPayfastCheckout(order.reference);
    } catch (e: any) {
      toast.error("Could not create order", { description: e?.message ?? String(e) });
      setSubmitting(false);
    }
  }

  if (llbAccessQ.isLoading) {
    return (
      <div className="container-narrow py-16 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Checking your LLB access…
      </div>
    );
  }

  if (llbAccessQ.data && !llbAccessQ.data.allowed) {
    return (
      <div className="container-narrow py-10 space-y-5">
        <Disclaimer variant="compact" />
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 text-amber-900">
          <div className="flex items-start gap-3">
            <Lock className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <h1 className="font-display text-2xl">LLB services are invitation-only</h1>
              <p className="mt-2 text-sm">
                LLB tutoring, module notes and tailored study material are only
                available to students we have contacted directly about LLB
                content. If you have received an LLB email from us on the
                address linked to this account, this page unlocks automatically.
              </p>
              <p className="mt-2 text-sm">
                Not on the list yet?{" "}
                <Link to="/contact" className="font-semibold underline">
                  Request LLB access
                </Link>{" "}
                and we'll email you the LLB welcome pack.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-narrow py-10 space-y-5">
      <Disclaimer variant="compact" />
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-md bg-navy/5 text-navy">
          <GraduationCap className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-display text-2xl text-navy md:text-3xl">UNISA LLB Tutoring</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Apply and pay to join tutored classes for any UNISA LLB module. Each module is{" "}
            <strong>{fmt(LLB_MODULE_PRICE_ZAR)}</strong> per semester.{" "}
            <strong>{LLB_BULK_DISCOUNT_THRESHOLD}+ modules = {Math.round(LLB_BULK_DISCOUNT_RATE * 100)}% off.</strong>{" "}
            Each enrolment unlocks pre-loaded notes, a study plan and daily AI tips by email.
          </p>
        </div>
      </div>

      {/* UNISA registration gate */}
      <UnisaRegistrationCard />

      {/* Today's tips for enrolled modules + history + regenerate */}
      <LlbTipsWidget />

      {/* Tailored AI notes for enrolled modules */}
      <LlbTailoredNotesPanel />




      {/* Semester */}
      <div className="mt-8 rounded-xl border border-border bg-card p-5">
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Semester
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {SEMESTERS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSemester(s.value)}
              className={`rounded-md border px-3 py-2 text-sm ${
                semester === s.value
                  ? "border-navy bg-navy text-navy-foreground"
                  : "border-input bg-background hover:border-navy"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Module picker grouped by level */}
      <div className="mt-6 space-y-6">
        {(["1", "2", "3", "4"] as const).map((lvl) => (
          <section key={lvl} className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg text-navy">Level {lvl} modules</h2>
            <div className="mt-3 divide-y divide-border">
              {grouped[lvl].map((m) => {
                const isSelected = selected.has(m.code);
                return (
                  <div key={m.code} className="py-3">
                    <div className="flex items-start gap-3">
                      <input
                        id={`mod-${m.code}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(m.code)}
                        className="mt-1.5 h-4 w-4 rounded border-input text-navy focus:ring-navy"
                      />
                      <label htmlFor={`mod-${m.code}`} className="flex-1 cursor-pointer">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs font-semibold text-navy">{m.code}</span>
                          <span className="text-sm font-medium">{m.title}</span>
                          {m.group ? (
                            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                              {m.group}
                            </span>
                          ) : null}
                          {m.semester && m.semester !== "either" ? (
                            <span className="rounded-full bg-gold/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-gold">
                              {m.semester}
                            </span>
                          ) : null}
                        </div>
                        {m.prerequisites ? (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Pre-req: {m.prerequisites}
                          </p>
                        ) : null}
                      </label>
                      <button
                        type="button"
                        onClick={() => setModalCode(m.code)}
                        className="text-xs font-medium text-navy hover:text-gold"
                      >
                        Details &amp; download
                      </button>
                    </div>
                  </div>
                );
              })}

            </div>
          </section>
        ))}
      </div>

      {/* PayFast Buy Now (R150 minimum per module, instant) */}
      <PayFastBuyNowLlb />

      {/* Sticky checkout summary */}
      <div className="mt-8 rounded-xl border border-navy/30 bg-navy text-navy-foreground p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.32em] text-gold">Your selection</p>
            <p className="mt-2 text-lg font-semibold">
              {selected.size} module{selected.size === 1 ? "" : "s"} · {semester}
            </p>
            <p className="mt-1 text-xs text-navy-foreground/70">
              Subtotal {fmt(pricing.subtotal)}
              {pricing.discountApplied
                ? ` − discount ${fmt(pricing.discount)} (${Math.round(LLB_BULK_DISCOUNT_RATE * 100)}% off ${LLB_BULK_DISCOUNT_THRESHOLD}+)`
                : null}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-navy-foreground/70">Total due</p>
            <p className="font-display text-3xl text-gold">{fmt(pricing.total)}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handlePay}
          disabled={selected.size === 0 || submitting || !hasUnisa}
          className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#13B5EA] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:brightness-95 disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          {submitting ? "Creating order…" : !hasUnisa ? "Add UNISA student number to continue" : `Pay ${fmt(pricing.total)} with PayFast`}
        </button>
        <p className="mt-2 text-[11px] text-navy-foreground/60">
          You'll be redirected straight to PayFast. Your tutor enrolments activate the moment payment
          is confirmed. A receipt is emailed to you automatically.
        </p>
      </div>

      {/* My orders */}
      <section className="mt-10 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2">
          <Receipt className="h-4 w-4 text-navy" />
          <h2 className="font-display text-lg text-navy">My tutoring orders</h2>
        </div>
        {ordersQ.isLoading ? (
          <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
        ) : !ordersQ.data?.orders?.length ? (
          <p className="mt-3 text-sm text-muted-foreground">No orders yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-border">
            {ordersQ.data.orders.map((o: any) => (
              <li key={o.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{o.reference}</p>
                  <p className="text-sm">
                    {o.module_count} modules · {o.semester} ·{" "}
                    <span className="font-mono text-xs">{(o.module_codes || []).join(", ")}</span>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">{fmt(Number(o.amount_zar))}</p>
                  <p
                    className={`text-xs ${
                      o.status === "paid" ? "text-emerald-600" : "text-amber-600"
                    }`}
                  >
                    {o.status === "paid" ? (
                      <span className="inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Paid
                      </span>
                    ) : (
                      <Link
                        to="/payment-status/$ref"
                        params={{ ref: o.reference }}
                        className="underline"
                      >
                        Pending — continue payment
                      </Link>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <LlbModuleDetailsModal module={modalModule} open={!!modalCode} onOpenChange={(o) => !o && setModalCode(null)} />
    </div>
  );
}

